import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { connect } from "react-redux";
import {
  HiOutlineArrowLeft,
  HiOutlineArrowRight,
  HiOutlineCamera,
  HiOutlineChevronDown,
  HiOutlineClipboardList,
  HiOutlineX,
  HiOutlineZoomIn,
} from "react-icons/hi";
import { FaEdit } from "react-icons/fa";
import AppointmentEditModal from "../../components/AppointmentEditModal";
import { CAR_DATA_UPDATE_STEPS } from "../../constant";
import { useContext } from "react";
import FeedbackContext from "../../context/cmscontext";
import { scrollTop } from "../../utils/window";
import { getAppointmentLocation } from "../../utils/appointmentBooking";
import { resolveMediaUrl } from "../../utils/userIdentity";
import RegPlate from "../../components/RegPlate";
import { resolveEvaluationPlateCountry } from "../../utils/plateCountry";

const HERO_IMAGE = "/images/hero-appointment-detail.jpg";

const PREVIOUS_OWNERS_LABELS = {
  1: "1",
  2: "2",
  3: "3",
  4: "4",
  5: "5 or more",
  6: "Don't know",
  7: "None",
};

const PHOTO_SECTIONS = [
  { key: "treads_images_", labelKey: "Treads_Images_text", icon: HiOutlineCamera },
  { key: "exterior_images_", labelKey: "Exterior_Images_text", icon: HiOutlineCamera },
  { key: "interior_images_", labelKey: "Interior_Images_text", icon: HiOutlineCamera },
  { key: "wheels_images_", labelKey: "Wheels_Images_text", icon: HiOutlineCamera },
  { key: "damage_images_", labelKey: "Damage_Images_text", icon: HiOutlineCamera },
];

function displayValue(value, fallback = "—") {
  if (value == null || value === "") {
    return fallback;
  }
  return String(value);
}

function formatFeatureAnswers(features = []) {
  if (!Array.isArray(features) || features.length === 0) {
    return "—";
  }
  return features.map((item) => item.choice).join(", ");
}

function photoSrc(item) {
  return resolveMediaUrl(item?.image || item?.url || item) || "";
}

function photoLabel(item, fallback = "Inspection photo") {
  return (
    item?.name ||
    item?.title ||
    item?.type ||
    item?.label ||
    fallback
  );
}

/** camera_url from user_evaluations / evaluation_create payloads. */
function getEvaluationCameraUrl(evaluation) {
  if (!evaluation || typeof evaluation !== "object") {
    return "";
  }

  const raw =
    evaluation.camera_url ||
    evaluation.Camera_url ||
    evaluation.cameraUrl ||
    evaluation.CAMERA_URL ||
    "";

  const url = String(raw || "").trim();
  if (!url) {
    return "";
  }

  if (
    url.startsWith("http://") ||
    url.startsWith("https://") ||
    url.startsWith("//")
  ) {
    return url.startsWith("//") ? `https:${url}` : url;
  }

  if (url.startsWith("/")) {
    return `${window.location.origin}${url}`;
  }

  return url;
}

function PhotoLightbox({ photos, index, onClose, onChange }) {
  const current = photos[index];
  const src = photoSrc(current);
  const label = [current?.__section, photoLabel(current)].filter(Boolean).join(" · ");
  const hasPrev = index > 0;
  const hasNext = index < photos.length - 1;

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowLeft" && hasPrev) onChange(index - 1);
      if (event.key === "ArrowRight" && hasNext) onChange(index + 1);
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [hasNext, hasPrev, index, onChange, onClose]);

  if (!current || !src) return null;

  return (
    <div
      className="pi-appointment-detail__lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Photo preview"
      onClick={onClose}
    >
      <div
        className="pi-appointment-detail__lightbox-panel"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="pi-appointment-detail__lightbox-bar">
          <div className="pi-appointment-detail__lightbox-meta">
            <strong>{label}</strong>
            <span>
              {index + 1} / {photos.length}
            </span>
          </div>
          <button
            type="button"
            className="pi-appointment-detail__lightbox-close"
            onClick={onClose}
            aria-label="Close preview"
          >
            <HiOutlineX size={22} />
          </button>
        </div>

        <div className="pi-appointment-detail__lightbox-stage">
          {hasPrev ? (
            <button
              type="button"
              className="pi-appointment-detail__lightbox-nav pi-appointment-detail__lightbox-nav--prev"
              onClick={() => onChange(index - 1)}
              aria-label="Previous photo"
            >
              <HiOutlineArrowLeft size={22} />
            </button>
          ) : null}
          <img src={src} alt={label} />
          {hasNext ? (
            <button
              type="button"
              className="pi-appointment-detail__lightbox-nav pi-appointment-detail__lightbox-nav--next"
              onClick={() => onChange(index + 1)}
              aria-label="Next photo"
            >
              <HiOutlineArrowRight size={22} />
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function PhotoAccordion({ title, images, defaultOpen = false, onPreview }) {
  const [open, setOpen] = useState(defaultOpen);
  const list = Array.isArray(images) ? images.filter((item) => photoSrc(item)) : [];
  const count = list.length;

  return (
    <div className={`pi-appointment-detail__accordion-item ${open ? "is-open" : ""}`}>
      <button
        type="button"
        className="pi-appointment-detail__accordion-trigger"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
      >
        <span className="pi-appointment-detail__accordion-leading">
          <span className="pi-appointment-detail__accordion-icon" aria-hidden>
            <HiOutlineCamera size={18} />
          </span>
          <span className="pi-appointment-detail__accordion-title">{title}</span>
        </span>
        <span className="pi-appointment-detail__accordion-trailing">
          <span
            className="pi-appointment-detail__accordion-count"
            aria-label={`${count} photo${count === 1 ? "" : "s"}`}
          >
            {count}
          </span>
          <HiOutlineChevronDown
            className="pi-appointment-detail__accordion-chevron"
            size={20}
            aria-hidden
          />
        </span>
      </button>
      {open ? (
        <div className="pi-appointment-detail__accordion-panel">
          {count > 0 ? (
            <div className="pi-appointment-detail__photos">
              {list.map((item, photoIndex) => {
                const src = photoSrc(item);
                const label = photoLabel(item, `${title} ${photoIndex + 1}`);
                return (
                  <button
                    type="button"
                    className="pi-appointment-detail__photo"
                    key={item.id || src || `${title}-${photoIndex}`}
                    onClick={() => onPreview?.(item)}
                    aria-label={`Preview ${label}`}
                  >
                    <img src={src} alt={label} loading="lazy" />
                    <span className="pi-appointment-detail__photo-overlay" aria-hidden>
                      <HiOutlineZoomIn size={20} />
                    </span>
                    <span className="pi-appointment-detail__photo-caption">{label}</span>
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="pi-appointment-detail__photos-empty">No photos uploaded yet.</p>
          )}
        </div>
      ) : null}
    </div>
  );
}

function DetailsTabs({ tabs, defaultTab }) {
  const [activeTab, setActiveTab] = useState(defaultTab || tabs[0]?.id);

  const current = tabs.find((tab) => tab.id === activeTab) || tabs[0];

  return (
    <section className="pi-appointment-detail__details">
      <div className="pi-appointment-detail__details-head">
        <h2>Vehicle information</h2>
        <div className="pi-appointment-detail__tabs" role="tablist" aria-label="Vehicle information">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`pi-appointment-detail__tab ${
                activeTab === tab.id ? "is-active" : ""
              }`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="pi-appointment-detail__tab-panel" role="tabpanel">
        {current?.action ? (
          <div className="pi-appointment-detail__tab-actions">{current.action}</div>
        ) : null}
        {current?.emptyMessage ? (
          <p className="pi-appointment-detail__notice">{current.emptyMessage}</p>
        ) : (
          <dl className="pi-appointment-detail__spec-list">
            {(current?.rows || []).map((row) => (
              <div className="pi-appointment-detail__spec-item" key={row.label}>
                <dt>{row.label}</dt>
                <dd>{row.value}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </section>
  );
}

function Myappointmentdetail({
  mycardata,
  appointment_update,
  add_car_data,
  intiate_data,
}) {
  const { feedback } = useContext(FeedbackContext);
  const page = feedback.myAppointment_Deatail_page || {};
  const location = useLocation();
  const history = useHistory();
  const locationState = location.state || {};
  const [index] = useState(locationState.index ?? 0);
  const [vehicleId] = useState(locationState.evaluationId ?? null);
  const [userId, setUserId] = useState("");
  const [showResults, setShowResults] = useState(false);

  const resolvedIndex = useMemo(() => {
    if (vehicleId != null) {
      const matchIndex = mycardata.findIndex(
        (item) => String(item?.id) === String(vehicleId)
      );
      if (matchIndex >= 0) {
        return matchIndex;
      }
    }

    return index;
  }, [mycardata, vehicleId, index]);

  const car = mycardata[resolvedIndex];
  const [previewIndex, setPreviewIndex] = useState(null);

  const allPhotos = useMemo(() => {
    if (!car) return [];
    const rows = [];
    PHOTO_SECTIONS.forEach((section) => {
      const sectionTitle = (page[section.labelKey] || section.labelKey).trim();
      const list = Array.isArray(car[section.key]) ? car[section.key] : [];
      list.forEach((item, photoIndex) => {
        if (!photoSrc(item)) return;
        rows.push({
          ...item,
          __section: sectionTitle,
          __key: `${section.key}-${item.id || photoSrc(item)}-${photoIndex}`,
        });
      });
    });
    return rows;
  }, [car, page]);

  const openPreview = useCallback(
    (item) => {
      const src = photoSrc(item);
      const nextIndex = allPhotos.findIndex(
        (photo) =>
          photo === item ||
          (item.id != null && photo.id === item.id) ||
          photoSrc(photo) === src
      );
      setPreviewIndex(nextIndex >= 0 ? nextIndex : 0);
    },
    [allPhotos]
  );

  const closePreview = useCallback(() => setPreviewIndex(null), []);
  const changePreview = useCallback((next) => setPreviewIndex(next), []);

  useEffect(() => {
    scrollTop();
  }, []);

  useEffect(() => {
    if (!mycardata.length) {
      history.replace("/myappointment");
    }
  }, [mycardata.length, history]);

  useEffect(() => {
    if (
      Object.keys(appointment_update.appointment_create || {}).length > 1 &&
      mycardata[resolvedIndex]
    ) {
      mycardata[resolvedIndex].appointment = appointment_update.appointment_create;
      setShowResults(false);
    }
  }, [appointment_update.appointment_create, resolvedIndex, mycardata]);

  const goToCarSteps = () => {
    if (!car) return;
    add_car_data(car);
    intiate_data(car);
    history.push(car?.id ? `/carsteps/${car.id}` : "/carsteps");
  };

  const goToTakePictures = () => {
    const cameraUrl = getEvaluationCameraUrl(car);
    if (!cameraUrl) return;
    window.open(cameraUrl, "_blank", "noopener,noreferrer");
  };

  const openAppointmentEdit = (appointmentId) => {
    setUserId(appointmentId);
    setShowResults(true);
  };

  if (showResults) {
    return (
      <div className="pi-appointment-detail">
        <AppointmentEditModal
          appointmentId={userId}
          updateIndex={resolvedIndex}
          onClose={() => setShowResults(false)}
        />
      </div>
    );
  }

  if (!car) {
    return null;
  }

  const appointment = car.appointment?.[0];
  const vehicleDetails = Array.isArray(car.vehcile_details) ? car.vehcile_details : [];
  const vehicleAddress = Array.isArray(car.vehicle_address) ? car.vehicle_address : [];
  const vehicleReg = car.reg_number || car.car || "";
  const plateCountry = resolveEvaluationPlateCountry({
    plateCountry: car.plate_country || car.Plate_country || car.country,
    evaluation: car,
    reg: vehicleReg,
  });
  const vehicleTitle = [car.brand_name, car.model_name, car.year_name]
    .filter(Boolean)
    .join(" ");
  const vehicleSubtitle = [car.variant_name, car.fuel_type].filter(Boolean).join(" · ");

  const appointmentRows = appointment
    ? [
        {
          label: page.My_Appointment_heading || "Location",
          value: displayValue(getAppointmentLocation(appointment)),
        },
        {
          label: page.Date_Selected_text || "Date",
          value: displayValue(appointment.date_selected),
        },
        {
          label: page.Time_Selected_text || "Time",
          value: displayValue(appointment.time_selected),
        },
      ]
    : [];

  const carRows = [
    { label: page.Brand_text || "Brand", value: displayValue(car.brand_name) },
    { label: page.Model_text || "Model", value: displayValue(car.model_name) },
    { label: page.Fuel_Type_text || "Fuel type", value: displayValue(car.fuel_type) },
    { label: page.Year_text || "Year", value: displayValue(car.year_name) },
    {
      label: page.Previou_Owners_text || "Previous owners",
      value: displayValue(PREVIOUS_OWNERS_LABELS[car.Previous_owners] || car.Previous_owners),
    },
    { label: page.Car_text || "Car", value: displayValue(car.car) },
    { label: page.Service_History_text || "Service history", value: displayValue(car.Service_history) },
    { label: page.Mileage_text || "Mileage", value: displayValue(car.Mileage) },
    { label: page.Variant_text || "Variant", value: displayValue(car.variant_name) },
  ];

  const userRows = [
    {
      label: page.User_Name_text || "Name",
      value: displayValue(car.Website_user?.toUpperCase?.() || car.Website_user),
    },
    {
      label: page.Email_text || "Email",
      value: displayValue(car.Email?.toLowerCase?.() || car.Email),
    },
    { label: page.Mobile_Number_text || "Mobile", value: displayValue(car.Mobile_number) },
    { label: page.Postcode_text || "Postcode", value: displayValue(car.Postcode) },
    { label: page.Tracking_Number_text || "Tracking", value: displayValue(car.tracking_number) },
    { label: page.car_notes || "Car notes", value: displayValue(car.car_notes) },
    { label: page.car_description || "Description", value: displayValue(car.car_description) },
    { label: page.refel_code || "Referral code", value: displayValue(car.refel_code) },
  ];

  const vehicleRows = vehicleDetails.map((item) => ({
    label: item.question_ || "Question",
    value: formatFeatureAnswers(item.feature),
  }));

  if (vehicleAddress.length > 0) {
    const savedCity =
      vehicleAddress[0].city || vehicleAddress[0].line3;
    vehicleRows.push(
      {
        label: page.Vehicle_Address || "Vehicle address",
        value: displayValue(vehicleAddress[0].address),
      },
      {
        label: page.City_text || "City",
        value: displayValue(savedCity),
      },
      {
        label: page.vechiclePostCodeLabel || "Vehicle postcode",
        value: displayValue(vehicleAddress[0].post_code),
      }
    );
  }

  const totalPhotos = allPhotos.length;
  const cameraUrl = getEvaluationCameraUrl(car);
  const showCameraCta = Boolean(cameraUrl) && totalPhotos === 0;
  const cameraHeading = page.Take_Pictures_heading || "Take pictures";
  const cameraDescription =
    page.Take_Pictures_text ||
    "Open the camera guide for this vehicle to capture inspection photos.";
  const cameraButtonLabel = page.Take_Pictures_button || "Take picture";

  const detailTabs = [
    {
      id: "booking",
      label: page.My_Appointment_heading?.trim() || "Booking",
      rows: appointmentRows,
      emptyMessage: appointment
        ? null
        : `${page.didntBookAppointment || "No appointment booked yet."} ${
            page.didntProvideInfoButton || "Complete your vehicle profile to schedule one."
          }`,
      action: appointment ? (
        <button
          type="button"
          className="pi-appointment-detail__tab-edit"
          onClick={() => openAppointmentEdit(appointment.id)}
        >
          <FaEdit size={14} aria-hidden />
          <span>Edit booking</span>
        </button>
      ) : null,
    },
    {
      id: "car",
      label: page.Car_Details_heading?.trim() || "Car",
      rows: carRows,
    },
    {
      id: "contact",
      label: page.User_Information_text?.trim() || "Contact",
      rows: userRows,
    },
    {
      id: "vehicle",
      label: page.vehicle_text?.trim() || "Profile",
      rows: vehicleRows,
      emptyMessage:
        vehicleRows.length > 0
          ? null
          : `No vehicle profile answers yet. Use ${page.Update_Car_Information || "Update Car Information"} to add them.`,
    },
  ];

  return (
    <div className="pi-appointment-detail">
      <header className="pi-inner-hero pi-inner-hero--with-bar">
        <div className="pi-inner-hero__media" aria-hidden="true">
          <img
            className="pi-inner-hero__media-img"
            src={`${process.env.PUBLIC_URL || ""}${HERO_IMAGE}`}
            alt=""
          />
          <span className="pi-inner-hero__media-shade" />
        </div>
        <div className="pi-inner-hero__fx" aria-hidden="true">
          <span className="pi-inner-hero__glow pi-inner-hero__glow--a" />
          <span className="pi-inner-hero__glow pi-inner-hero__glow--b" />
          <span className="pi-inner-hero__grid" />
        </div>
        <div className="pi-inner-hero__content">
          <div className="pi-inner-hero__copy">
            <p className="pi-label pi-inner-hero__label">Inspection record</p>
            <h1 className="pi-inner-hero__title">
              {vehicleTitle || page.My_Appointment_heading || "Vehicle details"}
            </h1>
            <p className="pi-inner-hero__lead">
              {vehicleSubtitle ||
                "Photos, booking details, and the vehicle profile linked to your appointment."}
            </p>
          </div>
        </div>
        <span className="pi-inner-hero__fade" aria-hidden="true" />
      </header>

      <div className="pi-appointment-detail__body">
        <div className="pi-appointment-detail__inner">
          <div className="pi-appointment-detail__toolbar">
            <button
              type="button"
              className="pi-appointment-detail__back"
              onClick={() => history.push("/myappointment")}
            >
              <HiOutlineArrowLeft size={18} aria-hidden />
              <span>Back to appointments</span>
            </button>
          </div>

          <section className="pi-appointment-detail__update-banner" aria-label="Update vehicle profile">
            <div className="pi-appointment-detail__update-banner-icon" aria-hidden>
              <HiOutlineClipboardList size={24} />
            </div>
            <div className="pi-appointment-detail__update-banner-copy">
              <h2>{page.Update_Car_Information || "Update car information"}</h2>
              <p>
                Opens <strong>Car Steps</strong> for this vehicle so you can finish or edit
                vehicle questions, location, photos, and booking — then return here when done.
              </p>
            </div>
            <button
              type="button"
              className="pi-appointment-detail__update-btn"
              name="CTAButton"
              onClick={goToCarSteps}
            >
              <span>Open Car Steps</span>
              <HiOutlineArrowRight size={18} aria-hidden />
            </button>
          </section>

          {showCameraCta ? (
            <section
              className="pi-appointment-detail__update-banner pi-appointment-detail__update-banner--camera"
              aria-label={cameraHeading}
            >
              <div
                className="pi-appointment-detail__update-banner-icon pi-appointment-detail__update-banner-icon--camera"
                aria-hidden
              >
                <HiOutlineCamera size={24} />
              </div>
              <div className="pi-appointment-detail__update-banner-copy">
                <h2>{cameraHeading}</h2>
                <p>{cameraDescription}</p>
              </div>
              <button
                type="button"
                className="pi-appointment-detail__update-btn pi-appointment-detail__update-btn--camera"
                name="TakePictureButton"
                onClick={goToTakePictures}
              >
                <HiOutlineCamera size={18} aria-hidden />
                <span>{cameraButtonLabel}</span>
                <HiOutlineArrowRight size={18} aria-hidden />
              </button>
            </section>
          ) : null}

          <section className="pi-appointment-detail__summary">
            <div className="pi-appointment-detail__summary-main">
              <h2>{vehicleTitle || "Your vehicle"}</h2>
              <p>
                {vehicleSubtitle ||
                  "Track inspection photos, appointment details, and submitted vehicle information."}
              </p>
            </div>
            <div className="pi-appointment-detail__summary-meta">
              <div className="pi-appointment-detail__stat pi-appointment-detail__stat--plate">
                <span className="pi-appointment-detail__stat-label">Registration</span>
                {vehicleReg ? (
                  <RegPlate
                    value={vehicleReg}
                    country={plateCountry}
                    className="pi-appointment-detail__plate"
                  />
                ) : (
                  <span className="pi-appointment-detail__stat-value">—</span>
                )}
              </div>
              <div className="pi-appointment-detail__stat">
                <span className="pi-appointment-detail__stat-label">Mileage</span>
                <span className="pi-appointment-detail__stat-value">
                  {displayValue(car.Mileage)}
                </span>
              </div>
              <div className="pi-appointment-detail__stat">
                <span className="pi-appointment-detail__stat-label">Appointment</span>
                <span className="pi-appointment-detail__stat-value">
                  {appointment ? displayValue(appointment.date_selected) : "Not booked"}
                </span>
              </div>
              <div className="pi-appointment-detail__stat">
                <span className="pi-appointment-detail__stat-label">Vehicle answers</span>
                <span className="pi-appointment-detail__stat-value">
                  {vehicleDetails.length}
                </span>
              </div>
            </div>
          </section>

          <section className="pi-appointment-detail__gallery" aria-label="Vehicle photos">
            <div className="pi-appointment-detail__gallery-head">
              <div>
                <h2>Inspection photos</h2>
                <p>
                  {totalPhotos} photos across {PHOTO_SECTIONS.length} categories
                  {totalPhotos > 0 ? " · Tap a photo to preview" : ""}
                </p>
              </div>
              {showCameraCta ? (
                <button
                  type="button"
                  className="pi-appointment-detail__camera-btn"
                  onClick={goToTakePictures}
                >
                  <HiOutlineCamera size={18} aria-hidden />
                  <span>{cameraButtonLabel}</span>
                </button>
              ) : null}
            </div>
            <div className="pi-appointment-detail__accordion">
              {PHOTO_SECTIONS.map((section, sectionIndex) => (
                <PhotoAccordion
                  key={section.key}
                  title={(page[section.labelKey] || section.labelKey).trim()}
                  images={car[section.key]}
                  defaultOpen={sectionIndex === 0}
                  onPreview={openPreview}
                />
              ))}
            </div>
          </section>

          <DetailsTabs tabs={detailTabs} defaultTab="car" />
        </div>
      </div>

      {previewIndex != null && allPhotos[previewIndex] ? (
        <PhotoLightbox
          photos={allPhotos}
          index={previewIndex}
          onClose={closePreview}
          onChange={changePreview}
        />
      ) : null}
    </div>
  );
}

const mapStateToProps = (state) => ({
  mycardata: state.myAppointment.data,
  appointment_update: state.sellMyCar,
});

const mapDispatchToProps = (dispatch) => ({
  add_car_data: (data) =>
    dispatch({
      type: CAR_DATA_UPDATE_STEPS,
      payload: {
        data,
        update: true,
      },
    }),
  intiate_data: (data) =>
    dispatch({
      type: "CAR_DETAIL_SETUP",
      payload: {
        data,
        data_vehicle: data,
        update: true,
      },
    }),
});

export default connect(mapStateToProps, mapDispatchToProps)(Myappointmentdetail);
